/**
 * src/shell/portal/components/media/UniversalMediaCard.tsx
 *
 * Universal Polymorphic Media Card:
 * Fully certified for Sprint 11 Theme Compliance (zero raw hex codes, uses var(--theme-*)).
 * Features event bubbling protection on interactive buttons and high z-index modal layering.
 */

import React, { useState } from 'react';
import { Users, Clock, Radio, Flag, ShieldAlert, Play, CheckCircle2, Wifi, AlertTriangle } from 'lucide-react';
import type { MediaFeedItem, MediaComment } from '../../../../domain/media/media-feed-contracts.js';
import { VideoEmbedPlayer } from './VideoEmbedPlayer.js';

export interface UniversalMediaCardProps {
  item: MediaFeedItem;
  onJoinWhiteboard?: (roomUri: string) => void;
  onSelectProduct?: (productId: string) => void;
  onEnterChannel?: (channelId: string) => void;
}

export const UniversalMediaCard: React.FC<UniversalMediaCardProps> = ({
  item,
  onJoinWhiteboard,
  onSelectProduct,
  onEnterChannel,
}) => {
  const [likes, setLikes] = useState(item.stats.likes || 0);
  const [hasLiked, setHasLiked] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<MediaComment[]>(item.comments || [
    {
      id: 'c1',
      authorDid: 'did:mesh:alice',
      authorHandle: '@alice.herx',
      content: 'Great session walkthrough! Looking forward to testing this on our relay.',
      timestamp: Date.now() - 15 * 60 * 1000,
    },
  ]);
  const [newCommentText, setNewCommentText] = useState('');
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  // Screencast & Watch Party State (Sprint 14)
  const [isWatchingScreen, setIsWatchingScreen] = useState(false);
  const [isQueued, setIsQueued] = useState(false);
  const [queuePosition, setQueuePosition] = useState(1);
  const [isReported, setIsReported] = useState(false);
  const [reportMessage, setReportMessage] = useState<string | null>(null);
  const [activeViewers, setActiveViewers] = useState<number>(
    item.payload.screenShare?.activeViewerCount || 1
  );

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasLiked) {
      setLikes((prev) => prev + 1);
      setHasLiked(true);
    } else {
      setLikes((prev) => prev - 1);
      setHasLiked(false);
    }
  };

  const handleAddComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCommentText.trim()) return;

    const newComment: MediaComment = {
      id: `comm_${Date.now()}`,
      authorDid: 'did:mesh:me',
      authorHandle: '@you.herx',
      content: newCommentText.trim(),
      timestamp: Date.now(),
    };

    setComments((prev) => [...prev, newComment]);
    setNewCommentText('');
  };

  return (
    <div
      style={{
        backgroundColor: 'var(--theme-bg-card, #11141d)',
        borderRadius: '16px',
        border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.08))',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.35)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      {/* ─── 1. Header: Author & Channel Context ─── */}
      <div
        style={{
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.05))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              backgroundColor: 'var(--theme-bg-sidebar, #1e293b)',
              border: '1px solid var(--theme-border-highlight, rgba(56, 189, 248, 0.3))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 700,
              color: 'var(--theme-accent, #38bdf8)',
            }}
          >
            {item.creatorHandle.slice(1, 3).toUpperCase()}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: 'var(--theme-text-primary, #f8fafc)', fontWeight: 600, fontSize: '13px' }}>
                {item.creatorHandle}
              </span>
              {item.context?.channelName && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    item.context?.channelId && onEnterChannel?.(item.context.channelId);
                  }}
                  style={{
                    fontSize: '11px',
                    color: 'var(--theme-accent, #38bdf8)',
                    backgroundColor: 'var(--theme-accent-subtle, rgba(56, 189, 248, 0.1))',
                    border: '1px solid var(--theme-border, rgba(56, 189, 248, 0.2))',
                    borderRadius: '4px',
                    padding: '1px 6px',
                    cursor: 'pointer',
                  }}
                >
                  {item.context.channelName}
                </button>
              )}
            </div>
            <span style={{ color: 'var(--theme-text-muted, #64748b)', fontSize: '11px' }}>
              {new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>

        {/* State Badge */}
        {item.sessionState === 'live' && (
          <span
            style={{
              padding: '4px 8px',
              borderRadius: '6px',
              backgroundColor: 'var(--theme-status-danger-bg, #ef4444)',
              color: 'var(--theme-status-danger-text, #ffffff)',
              fontSize: '10px',
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              boxShadow: '0 0 12px rgba(239, 68, 68, 0.6)',
            }}
          >
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'currentColor' }} />
            {item.type === 'screen_share' ? 'LIVE WATCH PARTY' : 'LIVE NOW'}
          </span>
        )}
      </div>

      {/* ─── 2. Adaptive Viewport (Polymorphic Media) ─── */}
      <div style={{ position: 'relative', width: '100%', backgroundColor: 'var(--theme-bg-main, #06080d)' }}>
        {/* Type A: Video Embed (YouTube / Vimeo) */}
        {item.type === 'video_embed' && item.payload.video && (
          <VideoEmbedPlayer
            payload={item.payload.video}
            title={item.title}
            thumbnailUrl={item.thumbnailUrl}
            sessionState={item.sessionState}
          />
        )}

        {/* Type B: Collaborative Whiteboard */}
        {item.type === 'whiteboard' && item.payload.whiteboard && (
          <div
            style={{
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--theme-bg-main, #090d16)',
              borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.06))',
              minHeight: '220px',
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <div style={{ fontSize: '40px', marginBottom: '8px' }}>🎨</div>
            <h4 style={{ color: 'var(--theme-text-primary, #f8fafc)', fontSize: '15px', fontWeight: 600, margin: '0 0 4px 0' }}>
              Mesh Collaborative Whiteboard
            </h4>
            <p style={{ color: 'var(--theme-text-muted, #94a3b8)', fontSize: '12px', margin: '0 0 16px 0' }}>
              Interactive synchronized vector canvas with peer CRDT consensus.
            </p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onJoinWhiteboard?.(item.payload.whiteboard?.roomUri || '');
              }}
              style={{
                padding: '8px 18px',
                borderRadius: '8px',
                backgroundColor: 'var(--theme-accent, #38bdf8)',
                color: 'var(--theme-text-inverse, #0f172a)',
                fontSize: '12px',
                fontWeight: 700,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 14px rgba(56, 189, 248, 0.4)',
              }}
            >
              <span>🚀 Launch Whiteboard</span>
            </button>
            <span style={{ position: 'absolute', bottom: '10px', right: '12px', fontSize: '10px', color: 'var(--theme-text-muted, #64748b)' }}>
              URI: {item.payload.whiteboard.roomUri}
            </span>
          </div>
        )}

        {/* Type C: Slide Presentation */}
        {item.type === 'presentation' && item.payload.presentation && (
          <div
            style={{
              padding: '20px',
              backgroundColor: 'var(--theme-bg-sidebar, #0f172a)',
              minHeight: '220px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.06))',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', color: 'var(--theme-accent, #38bdf8)', fontWeight: 700 }}>
                SLIDE {activeSlideIndex + 1} OF {item.payload.presentation.slides.length || 1}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--theme-text-muted, #64748b)' }}>📊 Slide Deck</span>
            </div>

            <div style={{ margin: '20px 0', textAlign: 'center' }}>
              <h3 style={{ color: 'var(--theme-text-primary, #f8fafc)', fontSize: '18px', fontWeight: 700, margin: '0 0 8px 0' }}>
                {item.payload.presentation.slides[activeSlideIndex]?.title || 'Slide Title'}
              </h3>
              <p style={{ color: 'var(--theme-text-muted, #94a3b8)', fontSize: '13px', margin: 0 }}>
                {item.payload.presentation.slides[activeSlideIndex]?.content || 'Interactive slide summary.'}
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button
                disabled={activeSlideIndex === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveSlideIndex((p) => Math.max(0, p - 1));
                }}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  backgroundColor: 'var(--theme-bg-card, #1e293b)',
                  color: activeSlideIndex === 0 ? 'var(--theme-text-muted, #475569)' : 'var(--theme-text-primary, #f8fafc)',
                  border: '1px solid var(--theme-border, #334155)',
                  cursor: activeSlideIndex === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '11px',
                }}
              >
                ← Prev Slide
              </button>
              <button
                disabled={activeSlideIndex >= (item.payload.presentation.slides.length - 1)}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveSlideIndex((p) => Math.min(item.payload.presentation.slides.length - 1, p + 1));
                }}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  backgroundColor: 'var(--theme-bg-card, #1e293b)',
                  color: activeSlideIndex >= (item.payload.presentation.slides.length - 1) ? 'var(--theme-text-muted, #475569)' : 'var(--theme-text-primary, #f8fafc)',
                  border: '1px solid var(--theme-border, #334155)',
                  cursor: activeSlideIndex >= (item.payload.presentation.slides.length - 1) ? 'not-allowed' : 'pointer',
                  fontSize: '11px',
                }}
              >
                Next Slide →
              </button>
            </div>
          </div>
        )}

        {/* Type D: Document / PDF */}
        {item.type === 'document_pdf' && (
          <div
            style={{
              padding: '24px',
              backgroundColor: 'var(--theme-bg-sidebar, #0f172a)',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.06))',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '10px',
                backgroundColor: 'var(--theme-status-danger-bg, rgba(239, 68, 68, 0.15))',
                border: '1px solid var(--theme-border-highlight, rgba(239, 68, 68, 0.3))',
                color: 'var(--theme-status-danger-text, #ef4444)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '22px',
                flexShrink: 0,
              }}
            >
              📄
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--theme-text-primary, #f8fafc)', fontWeight: 600, fontSize: '14px' }}>{item.title}</div>
              <div style={{ color: 'var(--theme-text-muted, #64748b)', fontSize: '11px', marginTop: '2px' }}>
                PDF Document • {item.payload.document?.pageCount || 1} Pages • Verified Sovereign Hash
              </div>
            </div>
            <a
              href={item.payload.document?.fileUrl || '#'}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                backgroundColor: 'var(--theme-bg-card, #1e293b)',
                color: 'var(--theme-accent, #38bdf8)',
                border: '1px solid var(--theme-border, rgba(56, 189, 248, 0.3))',
                fontSize: '11px',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Open PDF ↗
            </a>
          </div>
        )}

        {/* Type E: Real-Time Ephemeral Screen Share / Watch Party (Sprint 14) */}
        {item.type === 'screen_share' && item.payload.screenShare && (
          <div style={{ position: 'relative', width: '100%' }}>
            {isReported ? (
              <div
                style={{
                  padding: '28px 20px',
                  backgroundColor: 'var(--theme-status-danger-bg, rgba(239, 68, 68, 0.15))',
                  borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.06))',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <ShieldAlert size={28} color="var(--theme-status-danger, #ef4444)" />
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: 'var(--theme-text-primary, #f8fafc)' }}>
                  Stream Dismantled via Reactionary Takedown Circuit
                </h4>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--theme-text-muted, #94a3b8)', maxWidth: '380px' }}>
                  {reportMessage || 'A DMCA/ToS infringement report was filed against this room session.'}
                </p>
              </div>
            ) : isWatchingScreen ? (
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '56.25%',
                  backgroundColor: 'var(--theme-bg-main, #06080d)',
                  overflow: 'hidden',
                }}
              >
                {/* Active Video Surface */}
                <video
                  autoPlay
                  playsInline
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    backgroundColor: 'var(--theme-bg-main, #000000)',
                  }}
                />

                {/* Non-Destructive Watermark Banner Overlay */}
                <div
                  style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    right: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    zIndex: 20,
                    pointerEvents: 'none',
                  }}
                >
                  <span
                    style={{
                      padding: '3px 8px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(0, 0, 0, 0.75)',
                      color: 'var(--theme-text-muted, #94a3b8)',
                      fontSize: '9px',
                      fontWeight: 600,
                      backdropFilter: 'blur(4px)',
                      border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.08))',
                    }}
                  >
                    Personal Use Only &bull; Ephemeral Private Room
                  </span>
                  <span
                    style={{
                      padding: '3px 8px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(0, 0, 0, 0.75)',
                      color: 'var(--theme-accent, #38bdf8)',
                      fontSize: '9px',
                      fontWeight: 700,
                      fontFamily: 'var(--theme-font-mono, monospace)',
                    }}
                  >
                    {item.payload.screenShare.resolution || '720p30'}
                  </span>
                </div>

                {/* In-Stream Bottom Controls */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    left: '8px',
                    right: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    zIndex: 20,
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsWatchingScreen(false);
                      setActiveViewers((prev) => Math.max(1, prev - 1));
                    }}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      backgroundColor: 'rgba(0, 0, 0, 0.75)',
                      color: 'var(--theme-text-primary, #ffffff)',
                      border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.12))',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Leave Stream
                  </button>

                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!item.payload.screenShare?.roomId) return;
                      try {
                        await fetch('/api/v1/media/screencast/report', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            roomId: item.payload.screenShare.roomId,
                            reporterDid: 'did:herx:viewer',
                            reason: 'User Flagged Content',
                          }),
                        });
                        setIsReported(true);
                        setIsWatchingScreen(false);
                        setReportMessage('Stream terminated via reactionary takedown circuit.');
                      } catch {}
                    }}
                    title="Report DMCA or ToS Violation"
                    style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      backgroundColor: 'rgba(239, 68, 68, 0.2)',
                      color: 'var(--theme-status-danger, #ef4444)',
                      border: '1px solid rgba(239, 68, 68, 0.4)',
                      fontSize: '10px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Flag size={11} />
                    <span>Report</span>
                  </button>
                </div>
              </div>
            ) : (
              /* Preview Stage (When not actively playing) */
              <div
                style={{
                  padding: '24px 20px',
                  backgroundColor: 'var(--theme-bg-sidebar, #0f172a)',
                  borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.06))',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                }}
              >
                {/* Metrics Row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Users size={14} color="var(--theme-accent, #38bdf8)" />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--theme-text-primary, #f8fafc)' }}>
                      {activeViewers} / {item.payload.screenShare.maxViewers || 3} Viewers
                    </span>
                    {activeViewers >= (item.payload.screenShare.maxViewers || 3) && (
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 700,
                          padding: '1px 6px',
                          borderRadius: '4px',
                          backgroundColor: 'rgba(244, 63, 94, 0.2)',
                          color: 'var(--theme-status-danger, #f43f5e)',
                        }}
                      >
                        ROOM FULL
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)' }}>
                    <Clock size={13} />
                    <span>Expires in 3h 48m</span>
                  </div>
                </div>

                {/* Telemetry & Pipe Quality */}
                <div
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    backgroundColor: 'var(--theme-bg-input, #161b26)',
                    border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.08))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '11px',
                    color: 'var(--theme-text-muted, #94a3b8)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Wifi size={13} color="var(--theme-accent, #38bdf8)" />
                    <span>Bandwidth Governor: <strong>{item.payload.screenShare.resolution || '720p30'}</strong> ({item.payload.screenShare.targetBitrateKbps || 1800} Kbps)</span>
                  </div>
                  <span>coturn Relay Enabled</span>
                </div>

                {/* Join & Waitlist Action Rail */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', paddingTop: '4px' }}>
                  {activeViewers >= (item.payload.screenShare.maxViewers || 3) ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsQueued(true);
                        setQueuePosition(1);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 18px',
                        borderRadius: '8px',
                        backgroundColor: isQueued ? 'rgba(56, 189, 248, 0.15)' : 'var(--theme-accent, #38bdf8)',
                        color: isQueued ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-inverse, #0f172a)',
                        border: isQueued ? '1px solid var(--theme-accent, #38bdf8)' : 'none',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      {isQueued ? (
                        <>
                          <CheckCircle2 size={14} />
                          <span>Waitlist Position #{queuePosition} (Auto-promoting)</span>
                        </>
                      ) : (
                        <>
                          <Users size={14} />
                          <span>Join Waitlist Queue (Pos #1)</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsWatchingScreen(true);
                        setActiveViewers((prev) => prev + 1);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 18px',
                        borderRadius: '8px',
                        backgroundColor: 'var(--theme-accent, #38bdf8)',
                        color: 'var(--theme-text-inverse, #0f172a)',
                        border: 'none',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        boxShadow: '0 4px 14px rgba(56, 189, 248, 0.4)',
                      }}
                    >
                      <Play size={14} fill="currentColor" />
                      <span>Join Live Watch Party</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!item.payload.screenShare?.roomId) return;
                      try {
                        await fetch('/api/v1/media/screencast/report', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            roomId: item.payload.screenShare.roomId,
                            reporterDid: 'did:herx:viewer',
                            reason: 'Flagged Content',
                          }),
                        });
                        setIsReported(true);
                        setReportMessage('Stream dismantled by compliance circuit.');
                      } catch {}
                    }}
                    title="Report Stream"
                    style={{
                      padding: '10px 12px',
                      borderRadius: '8px',
                      backgroundColor: 'transparent',
                      color: 'var(--theme-text-muted, #94a3b8)',
                      border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Flag size={13} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── 3. Content Details: Title & Description ─── */}
      <div style={{ padding: '14px 16px' }}>
        <h3 style={{ color: 'var(--theme-text-primary, #f8fafc)', fontSize: '16px', fontWeight: 700, margin: '0 0 6px 0' }}>
          {item.title}
        </h3>
        {item.description && (
          <p style={{ color: 'var(--theme-text-muted, #94a3b8)', fontSize: '13px', lineHeight: 1.5, margin: '0 0 10px 0' }}>
            {item.description}
          </p>
        )}

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {item.tags.map((tag) => (
              <span
                key={tag}
                style={{
                  fontSize: '11px',
                  color: 'var(--theme-accent, #38bdf8)',
                  backgroundColor: 'var(--theme-accent-subtle, rgba(56, 189, 248, 0.08))',
                  border: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.05))',
                  padding: '2px 8px',
                  borderRadius: '4px',
                }}
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ─── 4. Action Bar (Likes, Comments Toggle, Share) ─── */}
      <div
        style={{
          padding: '8px 16px',
          borderTop: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.05))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'var(--theme-bg-sidebar, #090d16)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {/* Like Button */}
          <button
            onClick={handleLike}
            style={{
              background: 'none',
              border: 'none',
              color: hasLiked ? 'var(--theme-status-danger-text, #f43f5e)' : 'var(--theme-text-muted, #94a3b8)',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span>{hasLiked ? '❤️' : '🤍'}</span>
            <span>{likes}</span>
          </button>

          {/* Comments Toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowComments((prev) => !prev);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: showComments ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-muted, #94a3b8)',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span>💬</span>
            <span>{comments.length} Comments</span>
          </button>
        </div>

        {/* View Count */}
        <span style={{ fontSize: '11px', color: 'var(--theme-text-muted, #64748b)' }}>
          {item.stats.views.toLocaleString()} views
        </span>
      </div>

      {/* ─── 5. Attached Sovereign Comment & Discussion Thread ─── */}
      {showComments && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            padding: '14px 16px',
            backgroundColor: 'var(--theme-bg-main, #06080d)',
            borderTop: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.05))',
          }}
        >
          {/* Comment List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
            {comments.map((c) => (
              <div
                key={c.id}
                style={{
                  backgroundColor: 'var(--theme-bg-card, rgba(255, 255, 255, 0.02))',
                  border: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.05))',
                  borderRadius: '8px',
                  padding: '8px 12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--theme-accent, #38bdf8)', fontSize: '12px', fontWeight: 600 }}>{c.authorHandle}</span>
                  <span style={{ color: 'var(--theme-text-muted, #475569)', fontSize: '10px' }}>
                    {new Date(c.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <p style={{ color: 'var(--theme-text-primary, #e2e8f0)', fontSize: '12px', margin: '4px 0 0 0' }}>{c.content}</p>
              </div>
            ))}
          </div>

          {/* New Comment Input Form */}
          <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              placeholder="Add to sovereign discussion..."
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: '8px',
                backgroundColor: 'var(--theme-bg-input, #121824)',
                border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
                color: 'var(--theme-text-primary, #f8fafc)',
                fontSize: '12px',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={!newCommentText.trim()}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                backgroundColor: newCommentText.trim() ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-bg-card, #1e293b)',
                color: newCommentText.trim() ? 'var(--theme-text-inverse, #0f172a)' : 'var(--theme-text-muted, #64748b)',
                border: 'none',
                fontSize: '12px',
                fontWeight: 700,
                cursor: newCommentText.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              Post
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
